import {
  Box,
  Button,
  Flex,
  Group,
  Paper,
  Select,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { useDisclosure } from '@mantine/hooks';
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { ResourceModal } from './components/Resource.modal.tsx';

const PAGE_SIZES = [10, 15, 20];

interface GenericListComponentProps {
  name: string;
  columns: Array<{
    accessor: string;
    title?: string;
    render?: (record: any) => React.ReactNode;
  }>;
  children?: React.ReactNode;
  onRowClick?: ((record: any) => void) | null;
}

const GenericListComponentTanStack = forwardRef<
  { setSelectedRecord: (record: any) => void },
  GenericListComponentProps
>(({ name, columns, children, onRowClick = null }, ref) => {
  const [data, setData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState(''); // User input state
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
  const [selectedRecord, setSelectedRecord] = useState<any>();
  const [opened, { open, close }] = useDisclosure(false);

  const [searchParams] = useSearchParams();
  const fullQueryString = searchParams.toString(); // Always up-to-date
  const [finalQuery, setFinalQuery] = useState(''); // Stable debounced state for API call

  const latestFetchParams = useRef({ finalQuery, page, pageSize });
  const fetchTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isRequestInProgress = useRef(false);

  // **Expose setSelectedRecord to the Parent**
  useImperativeHandle(ref, () => ({
    setSelectedRecord,
  }));

  // Convert mantine-datatable columns to TanStack Table columns
  const tanstackColumns = useMemo<ColumnDef<any>[]>(() => {
    const columnHelper = createColumnHelper<any>();

    return columns.map(col => {
      if (col.render) {
        // For columns with custom render function
        return columnHelper.display({
          id: col.accessor,
          header: col.title || col.accessor,
          cell: ({ row }) => col.render!(row.original),
        });
      } else {
        // For regular accessor columns
        return columnHelper.accessor(col.accessor, {
          header: col.title || col.accessor,
          cell: info => info.getValue(),
        });
      }
    });
  }, [columns]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Function to fetch data
  const fetchData = useCallback(async () => {
    // Prevent multiple concurrent requests
    if (isRequestInProgress.current) {
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    isRequestInProgress.current = true;
    setLoading(true);

    let url = `${import.meta.env.VITE_API_URL}/services/admin/resource/${name}?page=${page}&size=${pageSize}`;
    if (finalQuery.trim().length > 0) {
      url += `&query=${encodeURIComponent(finalQuery)}`;
    }

    try {
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Handle different response structures
      if (result.data) {
        setData(result.data);
      } else if (Array.isArray(result)) {
        setData(result);
      } else {
        setData([]);
      }

      // Handle pagination info safely
      if (
        result.pagination &&
        typeof result.pagination.totalRecords === 'number'
      ) {
        setTotalRecords(result.pagination.totalRecords);
      } else if (
        result.totalRecords &&
        typeof result.totalRecords === 'number'
      ) {
        setTotalRecords(result.totalRecords);
      } else if (result.total && typeof result.total === 'number') {
        setTotalRecords(result.total);
      } else {
        // Fallback: use data length if no pagination info
        const dataArray = result.data || result || [];
        setTotalRecords(Array.isArray(dataArray) ? dataArray.length : 0);
      }
    } catch (error) {
      const aborted =
        (error instanceof Error || error instanceof DOMException) &&
        error.name === 'AbortError';
      if (aborted) {
        return;
      }
      console.error('Error fetching data:', error);
      setData([]);
      setTotalRecords(0);
    } finally {
      isRequestInProgress.current = false;
      setLoading(false);
    }
  }, [finalQuery, page, pageSize, name]);

  const callback = (item: any, isUpdate = false) => {
    console.info('callback', item, isUpdate);
    if (isUpdate) {
      setData(data.map(r => (r.id === item.id ? item : r)));
    } else {
      setData([item, ...data]);
    }
    close();
  };

  useEffect(() => {
    setSearchValue(fullQueryString); // Dynamically update `value` whenever `searchParams` changes
  }, [fullQueryString]); // Depend on fullQueryString to ensure updates

  useEffect(() => {
    if (selectedRecord) {
      open();
    }
  }, [selectedRecord, open]);

  useEffect(() => {
    if (!opened) {
      setSelectedRecord(undefined);
    }
  }, [opened]);

  // **Debounce search input (Wait before updating `finalQuery`)**
  useEffect(() => {
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    fetchTimeout.current = setTimeout(() => {
      setFinalQuery(searchValue || ''); // Update `finalQuery` only when user stops typing
    }, 400);

    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [searchValue]); // Only trigger when `searchValue` changes

  // **Trigger API fetch when `finalQuery` is stable**
  useEffect(() => {
    const shouldFetch =
      latestFetchParams.current.finalQuery !== finalQuery ||
      latestFetchParams.current.page !== page ||
      latestFetchParams.current.pageSize !== pageSize;

    if (shouldFetch || finalQuery === '') {
      latestFetchParams.current = { finalQuery, page, pageSize };
      fetchData();
    }
  }, [finalQuery, page, pageSize, fetchData]); // Only trigger when all values are fully updated

  // Cleanup function to abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, []);

  const handleRowClick = (record: any) => {
    if (onRowClick !== null) {
      onRowClick(record);
    } else {
      setSelectedRecord(record);
    }
  };

  const columnCount = tanstackColumns.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const startRecord = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalRecords);

  return (
    <Box>
      <ResourceModal
        opened={opened}
        close={close}
        name={name}
        callback={callback}
        selectedRecord={selectedRecord}
      />

      <Paper p='md' withBorder>
        <Flex
          justify='space-between'
          align='center'
          mb='md'
          gap='md'
          wrap='wrap'
        >
          <TextInput
            placeholder='Search...'
            leftSection={<Search size={16} />}
            flex={1}
            miw={200}
            value={searchValue}
            onChange={event => setSearchValue(event.currentTarget.value)}
          />
          <Group gap='xs' wrap='nowrap'>
            <Button
              variant='light'
              size='sm'
              onClick={() => void fetchData()}
              loading={loading}
              leftSection={<RefreshCw size={16} />}
            >
              Refresh
            </Button>
            <Button
              variant='light'
              size='sm'
              onClick={() => open()}
              leftSection={<Plus size={16} />}
            >
              Add
            </Button>
          </Group>
        </Flex>
        {children}

        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Table.Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {loading && data.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columnCount} style={{ textAlign: 'center' }}>
                  <Text size='sm' c='dimmed'>
                    Loading...
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columnCount} style={{ textAlign: 'center' }}>
                  <Text size='sm' c='dimmed'>
                    No records found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <Table.Tr
                  key={row.id}
                  onClick={() => handleRowClick(row.original)}
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <Table.Td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>

        <Flex
          justify='space-between'
          align='center'
          mt='md'
          gap='md'
          wrap='wrap'
        >
          <Group gap='xs'>
            <Text size='sm'>Records per page:</Text>
            <Select
              value={String(pageSize)}
              onChange={v => {
                if (v) {
                  setPageSize(Number(v));
                  setPage(1);
                }
              }}
              data={PAGE_SIZES.map(size => ({
                value: String(size),
                label: String(size),
              }))}
              w={80}
            />
          </Group>

          <Text size='sm' c='dimmed'>
            Showing {startRecord} to {endRecord} of {totalRecords} records
          </Text>

          <Group gap='xs'>
            <Button
              variant='light'
              size='sm'
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Text size='sm'>
              Page {page} of {totalPages}
            </Text>
            <Button
              variant='light'
              size='sm'
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages || totalRecords === 0}
            >
              Next
            </Button>
          </Group>
        </Flex>
      </Paper>
    </Box>
  );
});

export default GenericListComponentTanStack;
